import React, { CSSProperties, ReactNode } from 'react'
import { ProfilePicture } from '../generic'
import dayjs from 'dayjs'
import { Icon, IconType } from '@lmc/ui-kit'
import { Carer } from '@lmc/ts-common'

interface LastUpdateBoxProps {
  noTitle?: boolean
  carer?: Carer | null
  date?: string | null
  noDateText?: string
  style?: React.CSSProperties
  title?: string
  rightChildren?: React.ReactNode
  comment?: string | null
  children?: ReactNode
}

const LastUpdateBox: React.FC<LastUpdateBoxProps> = ({
  noTitle,
  carer,
  date,
  noDateText,
  style,
  title,
  rightChildren,
  comment
}) => {
  const hasDate = !!date
  const dateTime = dayjs(date).format('DD/MM/YYYY • HH:mm')

  const displayTitle = title || 'Last updated'
  const displayNoDate = noDateText || 'No updates yet'
  const displayDate = hasDate ? dateTime : displayNoDate

  return (
    <div
      className="lmc-card"
      style={{
        ...style,
        ...styles.mainContainer,
        ...(rightChildren && { alignContent: 'initial' })
      }}
    >
      <div style={styles.lastReview}>
        {!noTitle && <span style={styles.title}>{displayTitle}</span>}
        <div style={styles.dateContainer}>
          <Icon customIcon={IconType.Calendar} style={styles.calendarIcon} />
          <span id="last-review-date" style={styles.date}>
            {displayDate}
          </span>
        </div>
        {carer && hasDate && (
          <div style={styles.carerContainer}>
            <ProfilePicture
              id="carer-picture"
              url={carer.picture}
              style={styles.carerImage}
              type="handover__small"
            />
            <span style={styles.carerName} id="carer-name">
              {carer.name.first} {carer.name.last}
            </span>
          </div>
        )}
        {rightChildren && rightChildren}
      </div>
      {comment ? (
        <div style={styles.commentContainer}>
          <div style={styles.commentTitle}>Comment</div>
          <div style={styles.commentText as CSSProperties}>{comment}</div>
        </div>
      ) : null}
    </div>
  )
}

const styles = {
  carerContainer: {
    display: 'flex',
    margin: '3px 0px'
  },
  calendarIcon: { width: 33, height: 33 },
  carerImage: {
    alignSelf: 'center'
  },
  carerName: {
    alignSelf: 'center',
    paddingLeft: 10
  },
  commentContainer: {
    display: 'flex',
    flexFlow: 'row nowrap',
    minHeight: 20,
    margin: '16px 0px 10px 0px',
    alignItems: 'flex-start'
  },
  commentTitle: {
    alignSelf: 'flex-start',
    display: 'block',
    fontWeight: 'bold',
    margin: '0px 20px 0px 0px'
  },
  commentText: {
    paddingLeft: 20,
    whiteSpace: 'pre-wrap'
  },
  date: {
    alignSelf: 'center',
    display: 'block',
    paddingLeft: 15,
    color: '#000',
    opacity: 0.7
  },
  dateContainer: {
    display: 'flex',
    margin: '3px 50px 3px 0px'
  },
  dateIcon: {
    alignSelf: 'center',
    marginLeft: 5
  },
  mainContainer: {
    display: 'flex',
    flexFlow: 'column wrap',
    alignContent: 'space-between',
    padding: '5px 25px'
  },
  test: {
    alignSelf: 'center'
  },
  title: {
    alignSelf: 'center',
    display: 'block',
    fontWeight: 'bold',
    margin: '3px 20px 3px 0px'
  },
  lastReview: {
    display: 'flex',
    flexFlow: 'row wrap',
    minHeight: 42
  }
}

export default LastUpdateBox
